import { Music, User, LogOut, Search, Menu, Languages, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useState } from 'react';

type HeaderProps = {
  onSearch?: (query: string) => void;
  onLocationSearch?: (lat: number, lng: number, name: string) => void;
};

export function Header({ onSearch, onLocationSearch }: HeaderProps) {
  const { profile, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    // First try text search
    onSearch?.(searchQuery);

    // Then try location search with Mapbox Geocoding API
    if (onLocationSearch && searchQuery.trim()) {
      try {
        const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
        if (!mapboxToken) return;

        // Build query parameters for better accuracy
        // Mapbox supports: ar, de, en, es, fr, he, it, ja, ko, pt, ru, zh, zh-Hans, zh-Hant
        const params = new URLSearchParams({
          access_token: mapboxToken,
          limit: '1',
          types: 'place,region,postcode,address,poi,locality,neighborhood',
          language: language
        });

        // Add proximity if user location is available
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000 });
            });
            params.append('proximity', `${position.coords.longitude},${position.coords.latitude}`);
          } catch (error) {
            // Proximity is optional, continue without it
          }
        }

        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?${params}`
        );
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          const [lng, lat] = feature.center;
          onLocationSearch(lat, lng, feature.place_name);
        }
      } catch (error) {
        console.error('Location search error:', error);
      }
    }
  };

  return (
    <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src="/MapSound Logo_White copy.png"
              alt="MapSound"
              className="h-16 w-auto object-contain"
            />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-[#00c7b7] bg-clip-text text-transparent hidden sm:block">MapSound</h1>
          </div>

          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('header.search')}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent transition-all"
              />
            </div>
          </form>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-2 p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.display_name || profile.username}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-300" />
                </div>
              )}
              <Menu className="w-5 h-5 text-gray-300 hidden sm:block" />
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2">
                <div className="px-4 py-2 border-b border-gray-700">
                  <p className="font-semibold text-sm truncate text-white">
                    {profile?.display_name || profile?.username}
                  </p>
                  <p className="text-xs text-gray-400">@{profile?.username}</p>
                </div>

                <button
                  onClick={() => {
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">Profile</span>
                </button>

                <div className="border-t border-gray-700 pt-2 mt-2">
                  <div className="px-4 py-1 text-xs text-gray-400 font-semibold">
                    Language / 言語
                  </div>
                  <select
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value as any);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-sm bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors cursor-pointer border-none outline-none"
                  >
                    <option value="en">English</option>
                    <option value="ja">日本語 (Japanese)</option>
                    <option value="ko">한국어 (Korean)</option>
                  </select>
                </div>

                <button
                  onClick={() => {
                    signOut();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors flex items-center gap-2 text-red-400 hover:text-red-300"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">{t('header.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
